const {
  SOC_COOKIEBOT_CMP_LOADED_EVENT,
  isCookiebotCmpEnabled,
} = require("./cookiebotConsent");

const COOKIEBOT_CUSTOMIZATION_API_KEY = "__socCookiebot";
const COOKIEBOT_ACCEPT_ALL_TEXT = "Accept All";
const COOKIEBOT_DETAILS_SHOW_TEXT = "Cookie details";
const COOKIEBOT_DETAILS_HIDE_TEXT = "Hide cookie details";
const COOKIEBOT_DIALOG_ID = "CybotCookiebotDialog";
const COOKIEBOT_ACCEPT_ALL_BUTTON_ID =
  "CybotCookiebotDialogBodyLevelButtonLevelOptinAllowAll";
const COOKIEBOT_DETAILS_LINK_ID = "CybotCookiebotDialogBodyEdgeMoreDetailsLink";
const COOKIEBOT_LOCALE_STORAGE_KEY = "soc_lo";
const COOKIEBOT_SCRIPT_ID = "Cookiebot";
const COOKIEBOT_THIRD_PARTIES_LINK_ID = "CybotCookiebotDialogShowDetailsButton";
const COOKIEBOT_WIDGET_ID = "CookiebotWidget";
const CUSTOM_COOKIEBOT_BANNER_ID = "soc-cookie-banner";
const CUSTOM_COOKIEBOT_ACCEPT_ALL_BUTTON_IDS = [
  "soc-cookie-accept",
  "soc-cookie-accept-all",
];
const CUSTOM_COOKIEBOT_CLOSE_BUTTON_ID = "soc-cookie-close";
const CUSTOM_COOKIEBOT_CUSTOMIZE_BUTTON_ID = "soc-cookie-customize";
const CUSTOM_COOKIEBOT_DETAILS_ID = "soc-cookie-details";
const CUSTOM_COOKIEBOT_DENY_BUTTON_ID = "soc-cookie-deny";
const CUSTOM_COOKIEBOT_MARKETING_INPUT_ID = "soc-cookie-marketing";
const CUSTOM_COOKIEBOT_PREFERENCES_INPUT_ID = "soc-cookie-preferences";
const CUSTOM_COOKIEBOT_SAVE_BUTTON_ID = "soc-cookie-save";
const CUSTOM_COOKIEBOT_STATISTICS_INPUT_ID = "soc-cookie-statistics";
const CUSTOM_COOKIEBOT_CUSTOMIZING_CLASS = "is-customizing";
const CUSTOM_COOKIEBOT_WIRED_ATTRIBUTE = "data-soc-cookiebot-wired";
const DEFAULT_COOKIEBOT_CULTURE = "en";
const THIRD_PARTY_DISCLOSURE_ATTRIBUTE =
  "data-soc-cookiebot-third-party-disclosure";
const THIRD_PARTY_DISCLOSURE_PATTERNS = [
  /^We work with\s+\d+\s+third parties who may receive and process your information\.?$/i,
  /^Trabajamos con\s+\d+\s+terceros que pueden recibir y procesar su información\.?$/i,
  /^We work with\s+\d+\s+third parties\b/i,
  /^Trabajamos con\s+\d+\s+terceros\b/i,
  /\bWe work with\s+\d+\s+third parties\b/i,
  /\bTrabajamos con\s+\d+\s+terceros\b/i,
];
const THIRD_PARTY_DISCLOSURE_MAX_TEXT_LENGTH = 220;
const THIRD_PARTY_DISCLOSURE_TEXT_NODE_PATTERNS = [
  /We work with\s*/i,
  /\s*who may receive and process your information\.?/i,
  /Trabajamos con\s*/i,
  /\s*que pueden recibir y procesar su información\.?/i,
];
const COOKIEBOT_WIDGET_BRANDING_SELECTORS = [
  '#CookiebotWidget [data-consent="marketing"]',
  "#CookiebotWidget .CookiebotWidget-main-logo",
  "#CookiebotWidget .CookiebotWidget-main-logo *",
  '#CookiebotWidget a[href*="cookiebot.com"]',
  '#CookiebotWidget a[href*="usercentrics"]',
];

const getWindowObject = () =>
  typeof window !== "undefined" ? window : undefined;

const getDocumentObject = () =>
  typeof document !== "undefined" ? document : undefined;

const hasBrowserApis = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => Boolean(windowObject && documentObject && windowObject.document);

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

const normalizeCookiebotCulture = (value) => {
  const normalized = String(value || "").trim().toLowerCase();

  if (!normalized) return "";
  if (normalized === "es" || normalized.startsWith("es-")) return "es";
  if (normalized === "en" || normalized.startsWith("en-")) return "en";

  return "";
};

const readCookiebotCultureFromSearch = (windowObject = getWindowObject()) => {
  try {
    const params = new URLSearchParams(windowObject?.location?.search || "");
    return params.get("lang") || "";
  } catch (_error) {
    return "";
  }
};

const readCookiebotCultureFromStorage = (windowObject = getWindowObject()) => {
  try {
    return windowObject?.localStorage?.getItem(COOKIEBOT_LOCALE_STORAGE_KEY) || "";
  } catch (_error) {
    return "";
  }
};

const readCookiebotCultureFromHtml = (
  documentObject = getDocumentObject(),
) => documentObject?.documentElement?.getAttribute?.("lang") || "";

const readCookiebotCultureFromNavigator = (
  windowObject = getWindowObject(),
) => windowObject?.navigator?.language || "";

const resolveCookiebotCulture = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
  explicitLocale = "",
) => {
  const candidates = [
    explicitLocale,
    readCookiebotCultureFromSearch(windowObject),
    readCookiebotCultureFromStorage(windowObject),
    readCookiebotCultureFromHtml(documentObject),
    readCookiebotCultureFromNavigator(windowObject),
    DEFAULT_COOKIEBOT_CULTURE,
  ];

  for (const candidate of candidates) {
    const culture = normalizeCookiebotCulture(candidate);
    if (culture) return culture;
  }

  return DEFAULT_COOKIEBOT_CULTURE;
};

const getCookiebotScript = (documentObject = getDocumentObject()) =>
  documentObject?.getElementById?.(COOKIEBOT_SCRIPT_ID) || null;

const getCookiebotCulture = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (!hasBrowserApis(windowObject, documentObject)) return "";

  const scriptCulture =
    getCookiebotScript(documentObject)?.getAttribute?.("data-culture") || "";

  return normalizeCookiebotCulture(
    windowObject.__socCookiebotCulture || scriptCulture,
  );
};

const setCookiebotCulture = (
  culture,
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  const nextCulture =
    normalizeCookiebotCulture(culture) || DEFAULT_COOKIEBOT_CULTURE;

  if (!windowObject) return nextCulture;

  windowObject.__socCookiebotCulture = nextCulture;
  getCookiebotScript(documentObject)?.setAttribute?.("data-culture", nextCulture);
  return nextCulture;
};

const syncCookiebotCulture = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
  explicitLocale = "",
) =>
  setCookiebotCulture(
    resolveCookiebotCulture(windowObject, documentObject, explicitLocale),
    windowObject,
    documentObject,
  );

const shouldApplyEnglishDetailsLabels = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  const culture =
    getCookiebotCulture(windowObject, documentObject) ||
    resolveCookiebotCulture(windowObject, documentObject);
  return culture !== "es" && !culture.startsWith("es-");
};

const applyCookiebotDetailsLabels = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (!hasBrowserApis(windowObject, documentObject) || !windowObject.CookiebotDialog) {
    return false;
  }
  if (!shouldApplyEnglishDetailsLabels(windowObject, documentObject)) return false;

  windowObject.CookiebotDialog.showDetailsText = COOKIEBOT_DETAILS_SHOW_TEXT;
  windowObject.CookiebotDialog.hideDetailsText = COOKIEBOT_DETAILS_HIDE_TEXT;
  return true;
};

const isAllowAllLabel = (text) => /^Allow\s+All$/i.test(normalizeText(text));

const applyCookiebotAcceptAllLabel = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (!hasBrowserApis(windowObject, documentObject)) return false;
  if (!shouldApplyEnglishDetailsLabels(windowObject, documentObject)) return false;

  const dialog = documentObject.getElementById(COOKIEBOT_DIALOG_ID);
  const candidates = [
    documentObject.getElementById(COOKIEBOT_ACCEPT_ALL_BUTTON_ID),
    ...(dialog?.querySelectorAll
      ? Array.from(dialog.querySelectorAll("button, a, [role='button']"))
      : []),
  ].filter(Boolean);

  let didUpdate = false;
  new Set(candidates).forEach((element) => {
    if (isAllowAllLabel(element.textContent)) {
      element.textContent = COOKIEBOT_ACCEPT_ALL_TEXT;
      didUpdate = true;
    }

    if (isAllowAllLabel(element.getAttribute?.("aria-label"))) {
      element.setAttribute("aria-label", COOKIEBOT_ACCEPT_ALL_TEXT);
      didUpdate = true;
    }
  });

  return didUpdate;
};

const isThirdPartyDisclosureText = (text) =>
  THIRD_PARTY_DISCLOSURE_PATTERNS.some((pattern) => pattern.test(text));

const hideCookiebotSecondaryLinks = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (!hasBrowserApis(windowObject, documentObject)) return false;

  const links = [
    documentObject.getElementById(COOKIEBOT_DETAILS_LINK_ID),
    documentObject.getElementById(COOKIEBOT_THIRD_PARTIES_LINK_ID),
  ].filter(Boolean);

  links.forEach((link) => {
    link.setAttribute?.("aria-hidden", "true");
    link.setAttribute?.("tabindex", "-1");
    link.style?.setProperty?.("display", "none", "important");
  });

  return links.length > 0;
};

const hideCookiebotWidgetBranding = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (!hasBrowserApis(windowObject, documentObject)) return false;

  const elements = Array.from(
    documentObject.querySelectorAll?.(COOKIEBOT_WIDGET_BRANDING_SELECTORS.join(",")) ||
      [],
  );

  elements.forEach((element) => {
    element.setAttribute?.("hidden", "");
    element.setAttribute?.("aria-hidden", "true");
    element.setAttribute?.("tabindex", "-1");
    element.style?.setProperty?.("display", "none", "important");
    element.style?.setProperty?.("visibility", "hidden", "important");
  });

  return elements.length > 0;
};

const hideThirdPartyDisclosureTextNodes = (
  dialog,
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (
    !hasBrowserApis(windowObject, documentObject) ||
    typeof windowObject.NodeFilter === "undefined" ||
    typeof documentObject.createTreeWalker !== "function"
  ) {
    return false;
  }

  const walker = documentObject.createTreeWalker(
    dialog,
    windowObject.NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        const text = normalizeText(node.textContent);
        if (!text) return windowObject.NodeFilter.FILTER_REJECT;
        return THIRD_PARTY_DISCLOSURE_TEXT_NODE_PATTERNS.some((pattern) =>
          pattern.test(text),
        )
          ? windowObject.NodeFilter.FILTER_ACCEPT
          : windowObject.NodeFilter.FILTER_REJECT;
      },
    },
  );

  const nodes = [];
  while (walker.nextNode()) {
    nodes.push(walker.currentNode);
  }
  nodes.forEach((node) => {
    node.textContent = "";
  });
  return nodes.length > 0;
};

const hideCookiebotThirdPartyDisclosure = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (!hasBrowserApis(windowObject, documentObject)) return false;

  const dialog = documentObject.getElementById(COOKIEBOT_DIALOG_ID);
  if (!dialog) return false;

  hideThirdPartyDisclosureTextNodes(dialog, windowObject, documentObject);

  const candidates = Array.from(dialog.querySelectorAll("*"))
    .map((element) => ({
      element,
      text: normalizeText(element.textContent),
    }))
    .filter(
      ({ text }) =>
        text.length > 0 &&
        text.length <= THIRD_PARTY_DISCLOSURE_MAX_TEXT_LENGTH &&
        isThirdPartyDisclosureText(text),
    )
    .sort((a, b) => a.text.length - b.text.length);

  const target = candidates[0]?.element;
  if (!target) return false;

  target.setAttribute(THIRD_PARTY_DISCLOSURE_ATTRIBUTE, "true");
  target.style?.setProperty?.("display", "none", "important");
  return true;
};

const hasActiveCookiebot = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => Boolean(windowObject?.Cookiebot || getCookiebotScript(documentObject));

const reloadCookiebotOnCultureChange = (
  nextLocale = "",
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (!hasBrowserApis(windowObject, documentObject)) return false;

  const previousCulture =
    getCookiebotCulture(windowObject, documentObject) ||
    resolveCookiebotCulture(windowObject, documentObject);
  const nextCulture = syncCookiebotCulture(
    windowObject,
    documentObject,
    nextLocale,
  );

  if (
    !hasActiveCookiebot(windowObject, documentObject) ||
    nextCulture === previousCulture
  ) {
    return false;
  }

  windowObject.setTimeout?.(() => {
    windowObject.location?.reload?.();
  }, 0);
  return true;
};

const openCookiebotPreferences = (
  windowObject = getWindowObject(),
) => {
  if (typeof windowObject?.Cookiebot?.renew !== "function") return false;
  windowObject.Cookiebot.renew();
  return true;
};

const getCustomCookiebotBanner = (documentObject = getDocumentObject()) =>
  documentObject?.getElementById?.(CUSTOM_COOKIEBOT_BANNER_ID) || null;

const getCookiebotWidget = (documentObject = getDocumentObject()) =>
  documentObject?.getElementById?.(COOKIEBOT_WIDGET_ID) || null;

const removeInlineHiddenStyles = (element) => {
  element?.style?.removeProperty?.("display");
  element?.style?.removeProperty?.("visibility");
};

const preserveCookiebotWidget = (
  _windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  const widget = getCookiebotWidget(documentObject);
  if (!widget) return false;

  widget.removeAttribute?.("hidden");
  widget.removeAttribute?.("inert");
  widget.removeAttribute?.("aria-hidden");
  removeInlineHiddenStyles(widget);
  return true;
};

const scheduleCookiebotWidgetPreservation = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  const didPreserve = preserveCookiebotWidget(windowObject, documentObject);
  windowObject?.setTimeout?.(
    () => preserveCookiebotWidget(windowObject, documentObject),
    0,
  );
  windowObject?.setTimeout?.(
    () => preserveCookiebotWidget(windowObject, documentObject),
    100,
  );
  return didPreserve;
};

const showCustomCookiebotBanner = (
  _windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  const banner = getCustomCookiebotBanner(documentObject);
  if (!banner) return false;

  banner.removeAttribute?.("hidden");
  banner.removeAttribute?.("aria-hidden");
  banner.style?.removeProperty?.("display");
  return true;
};

const hideCustomCookiebotBanner = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  const banner = getCustomCookiebotBanner(documentObject);
  const details = documentObject?.getElementById?.(CUSTOM_COOKIEBOT_DETAILS_ID);
  const denyButton = documentObject?.getElementById?.(CUSTOM_COOKIEBOT_DENY_BUTTON_ID);

  banner?.classList?.remove?.(CUSTOM_COOKIEBOT_CUSTOMIZING_CLASS);
  banner?.setAttribute?.("hidden", "hidden");
  banner?.setAttribute?.("aria-hidden", "true");
  banner?.style?.setProperty?.("display", "none", "important");
  details?.setAttribute?.("hidden", "hidden");
  denyButton?.setAttribute?.("hidden", "hidden");
  windowObject?.Cookiebot?.hide?.();
  scheduleCookiebotWidgetPreservation(windowObject, documentObject);
  return Boolean(banner || windowObject?.Cookiebot);
};

const showCustomCookiebotDetails = (
  _windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  const banner = getCustomCookiebotBanner(documentObject);
  const details = documentObject?.getElementById?.(CUSTOM_COOKIEBOT_DETAILS_ID);
  const denyButton = documentObject?.getElementById?.(CUSTOM_COOKIEBOT_DENY_BUTTON_ID);

  banner?.classList?.add?.(CUSTOM_COOKIEBOT_CUSTOMIZING_CLASS);
  details?.removeAttribute?.("hidden");
  denyButton?.removeAttribute?.("hidden");

  return Boolean(banner || details || denyButton);
};

const submitCustomCookiebotConsent = (
  preferences,
  statistics,
  marketing,
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  const submitCustomConsent = windowObject?.Cookiebot?.submitCustomConsent;
  if (typeof submitCustomConsent === "function") {
    submitCustomConsent.call(windowObject.Cookiebot, preferences, statistics, marketing);
  }

  hideCustomCookiebotBanner(windowObject, documentObject);
  return typeof submitCustomConsent === "function";
};

const readCustomCookiebotSelection = (
  customInputId,
  fallbackInputIds = [],
  documentObject = getDocumentObject(),
) => {
  const inputIds = [customInputId, ...fallbackInputIds].filter(Boolean);

  for (const inputId of inputIds) {
    const input = documentObject?.getElementById?.(inputId);
    if (typeof input?.checked === "boolean") return input.checked;
  }

  return false;
};

const saveCustomCookiebotConsent = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) =>
  submitCustomCookiebotConsent(
    readCustomCookiebotSelection(
      CUSTOM_COOKIEBOT_PREFERENCES_INPUT_ID,
      [
        "CybotCookiebotDialogBodyLevelButtonPreferences",
        "CybotCookiebotDialogBodyLevelButtonPreferencesInline",
      ],
      documentObject,
    ),
    readCustomCookiebotSelection(
      CUSTOM_COOKIEBOT_STATISTICS_INPUT_ID,
      [
        "CybotCookiebotDialogBodyLevelButtonStatistics",
        "CybotCookiebotDialogBodyLevelButtonStatisticsInline",
      ],
      documentObject,
    ),
    false,
    windowObject,
    documentObject,
  );

const acceptAllCustomCookiebotConsent = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => submitCustomCookiebotConsent(true, true, false, windowObject, documentObject);

const denyCustomCookiebotConsent = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => submitCustomCookiebotConsent(false, false, false, windowObject, documentObject);

const wireCustomCookiebotButton = (
  buttonIds,
  handler,
  documentObject = getDocumentObject(),
) => {
  return [buttonIds]
    .flat()
    .filter(Boolean)
    .map((buttonId) => {
      const button = documentObject?.getElementById?.(buttonId);
      if (!button || typeof button.addEventListener !== "function") return false;
      if (button.getAttribute?.(CUSTOM_COOKIEBOT_WIRED_ATTRIBUTE) === "true") {
        return false;
      }

      button.addEventListener("click", (event) => {
        event?.preventDefault?.();
        handler();
      });
      button.setAttribute?.(CUSTOM_COOKIEBOT_WIRED_ATTRIBUTE, "true");
      return true;
    })
    .some(Boolean);
};

const wireCustomCookiebotBannerHandlers = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  const handlers = [
    [
      CUSTOM_COOKIEBOT_ACCEPT_ALL_BUTTON_IDS,
      () => acceptAllCustomCookiebotConsent(windowObject, documentObject),
    ],
    [
      CUSTOM_COOKIEBOT_DENY_BUTTON_ID,
      () => denyCustomCookiebotConsent(windowObject, documentObject),
    ],
    [
      CUSTOM_COOKIEBOT_SAVE_BUTTON_ID,
      () => saveCustomCookiebotConsent(windowObject, documentObject),
    ],
    [
      CUSTOM_COOKIEBOT_CLOSE_BUTTON_ID,
      () => hideCustomCookiebotBanner(windowObject, documentObject),
    ],
    [
      CUSTOM_COOKIEBOT_CUSTOMIZE_BUTTON_ID,
      () => showCustomCookiebotDetails(windowObject, documentObject),
    ],
  ];

  return handlers
    .map(([buttonId, handler]) =>
      wireCustomCookiebotButton(buttonId, handler, documentObject),
    )
    .some(Boolean);
};

const exposeCookiebotCustomTemplateApi = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (!windowObject) return null;

  windowObject.showSocCookieBanner = () =>
    showCustomCookiebotBanner(windowObject, documentObject);
  windowObject.hideSocCookieBanner = () =>
    hideCustomCookiebotBanner(windowObject, documentObject);
  windowObject.showSocCookieDetails = () =>
    showCustomCookiebotDetails(windowObject, documentObject);
  windowObject.customizeSocCookies = windowObject.showSocCookieDetails;
  windowObject.handleSocCookieAccept = () =>
    acceptAllCustomCookiebotConsent(windowObject, documentObject);
  windowObject.acceptAllSocCookies = () =>
    acceptAllCustomCookiebotConsent(windowObject, documentObject);
  windowObject.denySocCookies = () =>
    denyCustomCookiebotConsent(windowObject, documentObject);
  windowObject.saveSocCookiePreferences = () =>
    saveCustomCookiebotConsent(windowObject, documentObject);
  windowObject.saveSocCookieSelection = windowObject.saveSocCookiePreferences;
  windowObject.closeSocCookieBanner = () =>
    hideCustomCookiebotBanner(windowObject, documentObject);
  wireCustomCookiebotBannerHandlers(windowObject, documentObject);

  return {
    acceptAllSocCookies: windowObject.acceptAllSocCookies,
    closeSocCookieBanner: windowObject.closeSocCookieBanner,
    customizeSocCookies: windowObject.customizeSocCookies,
    denySocCookies: windowObject.denySocCookies,
    handleSocCookieAccept: windowObject.handleSocCookieAccept,
    hideSocCookieBanner: windowObject.hideSocCookieBanner,
    saveSocCookiePreferences: windowObject.saveSocCookiePreferences,
    saveSocCookieSelection: windowObject.saveSocCookieSelection,
    showSocCookieBanner: windowObject.showSocCookieBanner,
    showSocCookieDetails: windowObject.showSocCookieDetails,
  };
};

const enhanceCookiebotDialog = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  syncCookiebotCulture(windowObject, documentObject);
  applyCookiebotDetailsLabels(windowObject, documentObject);
  applyCookiebotAcceptAllLabel(windowObject, documentObject);
  hideCookiebotSecondaryLinks(windowObject, documentObject);
  hideCookiebotThirdPartyDisclosure(windowObject, documentObject);
  hideCookiebotWidgetBranding(windowObject, documentObject);
  preserveCookiebotWidget(windowObject, documentObject);
  wireCustomCookiebotBannerHandlers(windowObject, documentObject);
};

const buildCookiebotCustomizationApi = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) =>
  Object.freeze({
    applyDetailsLabels: () =>
      applyCookiebotDetailsLabels(windowObject, documentObject),
    enhanceDialog: () => enhanceCookiebotDialog(windowObject, documentObject),
    getActiveCulture: () =>
      getCookiebotCulture(windowObject, documentObject) ||
      resolveCookiebotCulture(windowObject, documentObject),
    hideSecondaryLinks: () =>
      hideCookiebotSecondaryLinks(windowObject, documentObject),
    hideThirdPartyDisclosure: () =>
      hideCookiebotThirdPartyDisclosure(windowObject, documentObject),
    hideWidgetBranding: () =>
      hideCookiebotWidgetBranding(windowObject, documentObject),
    preserveWidget: () => preserveCookiebotWidget(windowObject, documentObject),
    customTemplate: () =>
      exposeCookiebotCustomTemplateApi(windowObject, documentObject),
    openPreferences: () => openCookiebotPreferences(windowObject),
    reloadForLanguageChange: (nextLocale = "") =>
      reloadCookiebotOnCultureChange(nextLocale, windowObject, documentObject),
    resolveCulture: (nextLocale = "") =>
      resolveCookiebotCulture(windowObject, documentObject, nextLocale),
    syncLanguage: (nextLocale = "") =>
      syncCookiebotCulture(windowObject, documentObject, nextLocale),
  });

const exposeCookiebotCustomizationApi = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (!windowObject) return null;

  const api = buildCookiebotCustomizationApi(windowObject, documentObject);
  windowObject[COOKIEBOT_CUSTOMIZATION_API_KEY] = api;
  exposeCookiebotCustomTemplateApi(windowObject, documentObject);
  return api;
};

const initializeCookiebotBannerEnhancer = (
  windowObject = getWindowObject(),
  documentObject = getDocumentObject(),
) => {
  if (
    !hasBrowserApis(windowObject, documentObject) ||
    (!isCookiebotCmpEnabled() && !hasActiveCookiebot(windowObject, documentObject))
  ) {
    return () => {};
  }

  const listener = (event = {}) => {
    syncCookiebotCulture(
      windowObject,
      documentObject,
      event?.detail?.locale || "",
    );
    enhanceCookiebotDialog(windowObject, documentObject);
  };

  exposeCookiebotCustomizationApi(windowObject, documentObject);

  if (windowObject.__socCookiebotBannerEnhancerInitialized) {
    listener();
    return () => {};
  }

  windowObject.__socCookiebotBannerEnhancerInitialized = true;
  windowObject.addEventListener("CookiebotOnDialogInit", listener);
  windowObject.addEventListener("CookiebotOnDialogDisplay", listener);
  windowObject.addEventListener(SOC_COOKIEBOT_CMP_LOADED_EVENT, listener);
  windowObject.addEventListener("soc:locale-change", listener);
  listener();
  windowObject.setTimeout(() => listener(), 100);
  windowObject.setTimeout(() => listener(), 500);

  const observer =
    typeof windowObject.MutationObserver === "function"
      ? new windowObject.MutationObserver(listener)
      : null;

  observer?.observe(documentObject.documentElement, {
    childList: true,
    subtree: true,
  });
  windowObject.setTimeout(() => observer?.disconnect?.(), 5000);

  return () => {
    observer?.disconnect?.();
    windowObject.removeEventListener("CookiebotOnDialogInit", listener);
    windowObject.removeEventListener("CookiebotOnDialogDisplay", listener);
    windowObject.removeEventListener(SOC_COOKIEBOT_CMP_LOADED_EVENT, listener);
    windowObject.removeEventListener("soc:locale-change", listener);
  };
};

module.exports = {
  COOKIEBOT_CUSTOMIZATION_API_KEY,
  COOKIEBOT_ACCEPT_ALL_TEXT,
  COOKIEBOT_DETAILS_HIDE_TEXT,
  COOKIEBOT_DETAILS_SHOW_TEXT,
  COOKIEBOT_LOCALE_STORAGE_KEY,
  DEFAULT_COOKIEBOT_CULTURE,
  THIRD_PARTY_DISCLOSURE_ATTRIBUTE,
  applyCookiebotAcceptAllLabel,
  applyCookiebotDetailsLabels,
  acceptAllCustomCookiebotConsent,
  buildCookiebotCustomizationApi,
  denyCustomCookiebotConsent,
  enhanceCookiebotDialog,
  exposeCookiebotCustomTemplateApi,
  exposeCookiebotCustomizationApi,
  getCookiebotCulture,
  hideCookiebotSecondaryLinks,
  hideCookiebotThirdPartyDisclosure,
  hideCookiebotWidgetBranding,
  hideCustomCookiebotBanner,
  initializeCookiebotBannerEnhancer,
  normalizeCookiebotCulture,
  openCookiebotPreferences,
  preserveCookiebotWidget,
  reloadCookiebotOnCultureChange,
  resolveCookiebotCulture,
  saveCustomCookiebotConsent,
  setCookiebotCulture,
  showCustomCookiebotBanner,
  showCustomCookiebotDetails,
  syncCookiebotCulture,
  wireCustomCookiebotBannerHandlers,
};
